/**
 * Preview-only sample data for the Job Title modal.
 * The real Hiring Insights numbers are injected at serving time by Post-Press from a RAD artifact,
 * which is only available to Post-Press on indeed.com / qa.indeed.net.
 * On the WP Engine origin (indeeddev.wpengine.com) and in local dev (npm run dev), the data is absent,
 * so the modal would otherwise render nothing or NaN for the numbers.
 *
 * To keep the modal viewable for editors and local development, we render clearly-labeled sample data instead,
 * — but only on non-production preview hosts.
 *
 * IMPORTANT: the theme is built once and that single bundle is served to BOTH the WP Engine preview
 * origin and indeed.com (Post-Press proxies the same HTML/assets), so everything reachable from this
 * module — including the mockData files — ships in the production bundle. Only ever put fabricated,
 * non-sensitive sample data here. Anything real or sensitive must come from Post-Press/server
 * injection instead, never from this file.
 */

import enUS from './mockData/en_US';
import jaJP from './mockData/ja_JP';
import { i18nDateFormat, replaceMonthPlaceholder } from './utils/localizationUtils';

type AnyRecord = Record<string, any>;

// Production is only ever served under indeed.com / indeed.net (Post-Press proxies the WP Engine
// origin onto those hosts, including qa.indeed.net). We gate on those PUBLIC prod hosts rather than
// naming the internal WP Engine origins, so internal hostnames don't ship in the production bundle.
const PROD_HOST_RE = /(^|\.)indeed\.(com|net)$/;

// Treats any host that isn't a known production indeed host as a non-production preview host: the WP
// Engine origins (indeeddev*.wpengine.com / .test) and local dev. Note the failure mode is inverted
// vs. an allowlist — an unknown host is treated as preview — which is safe as long as production is
// only served under indeed.(com|net).
export const isNonProdPreviewHost = (): boolean => {
  return !PROD_HOST_RE.test(window.location.hostname);
};

// The `npm run dev` server (localhost) is used for component development, where the "sample data"
// notice is just noise. We still fabricate the data there so the modal renders, but suppress the
// notice — it's only meaningful on the WP preview origins that editors actually browse.
const isLocalDevHost = (): boolean => {
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

// The sample numbers live in the mockData files (shared with `npm run dev`). Pick the locale that
// best matches the page; everything else falls back to en_US since the numbers are locale-agnostic
// and the labels/localization come from ACF on WP pages.
const sampleSource = (locale?: string) => {
  return locale && locale.toLowerCase().startsWith('ja') ? jaJP : enUS;
};

// Top-level modalData fields we overlay from ACF when WordPress provided them, keeping the
// sample defaults for any that are missing/empty.
const ACF_PASSTHROUGH_FIELDS = [
  'localization',
  'sourceName',
  'sourceDate',
  'modalTitleSuffix',
  'ctas',
] as const;

// Per-section ACF string fields copied verbatim when present (tooltip_info is handled separately
// because it needs the source-date substitution).
const ACF_SECTION_STRING_FIELDS = ['label', 'description'] as const;

// ACF sections can carry handcrafted numbers on the title-fallback path (used when a norm title
// can't be resolved but a per-title Job Title Insights page exists). We prefer those real values
// and only fabricate the ones ACF is missing, so preview never clobbers real data.
const PREFER_ACF_METRIC_FIELDS: Record<string, string[]> = {
  job_seekers: ['amount', 'change_amount'],
  average_salary: ['unit', 'low', 'average', 'high'],
  employers: ['amount'],
  resumes: ['amount', 'change_amount'],
};

const hasAcfValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== null && value !== undefined && value !== '';
};

// Builds sample modalData. When WordPress provided real ACF data (WP preview), its localization,
// CTAs, source name/date, title suffix, and per-section labels/tooltips/descriptions are kept and
// overlaid onto the sample numbers so the modal stays localized and realistic.
const buildSampleModalData = (acf?: AnyRecord): AnyRecord => {
  const source = sampleSource(acf?.localization?.locale);
  const modalData: AnyRecord = JSON.parse(JSON.stringify(source.modalData));

  if (acf) {
    // hasAcfValue covers both empty strings and empty arrays (ctas).
    ACF_PASSTHROUGH_FIELDS.forEach((field) => {
      if (hasAcfValue(acf[field])) {
        modalData[field] = acf[field];
      }
    });

    const formattedDate = i18nDateFormat(modalData.sourceDate, modalData.localization.locale);
    modalData.sections = modalData.sections.map((section: AnyRecord) => {
      const acfSection = acf.sections?.find(
        (s: AnyRecord) => s && s.name === section.name
      );

      if (acfSection) {
        // Keep the real, localized ACF strings.
        ACF_SECTION_STRING_FIELDS.forEach((field) => {
          if (acfSection[field]) {
            section[field] = acfSection[field];
          }
        });

        if (acfSection.tooltip_info) {
          section.tooltip_info = replaceMonthPlaceholder(acfSection.tooltip_info, formattedDate);
        }
        // Keep any real handcrafted metric numbers; only the missing ones stay sample.
        (PREFER_ACF_METRIC_FIELDS[section.name] || []).forEach((field) => {
          if (hasAcfValue(acfSection[field])) {
            section[field] = acfSection[field];
          }
        });
      }

      return section;
    });
  }

  // Flags the UI to render the "Sample data" notice — on the WP preview origins, but not on the
  // local dev server. Dates and search terms are always illustrative on preview (Post-Press
  // supplies them in production), so the notice stays on even when some metric numbers came from
  // real ACF values.
  modalData.showSampleNotice = !isLocalDevHost();
  return modalData;
};

// Single entry point for both local dev (no WP data) and WP preview hosts. Ensures window[id]
// holds renderable, clearly-labeled sample data and returns the props to render. Returns the
// untouched props (or null) when not on a preview host, so prod/qa behavior is unchanged.
export const applyPreviewSampleData = (id: string): AnyRecord | null => {
  // If not a preview host, return the existing props or null if not present.
  if (!isNonProdPreviewHost()) {
    return (window as any)[id] ?? null;
  }

  // window[id] may be the WordPress-provided props OR a DOM element that the browser auto-assigned
  // to the global because the mount node shares the same id. Only real WP props expose `modalData`.
  const existing: AnyRecord | undefined = (window as any)[id];
  const acf: AnyRecord | undefined = existing?.modalData;

  // Reuse the WP props when present (keeps its title/display); otherwise build fresh sample props
  // and seed title/display from the sample source (local dev has no WordPress-provided values).
  const props: AnyRecord = acf ? existing! : {};
  if (!acf) {
    const source = sampleSource();
    props.title = source.title;
    props.display = source.display;
  }

  // Build the sample modal data.
  props.modalData = buildSampleModalData(acf);

  // Assign the props to the window[id] global.
  (window as any)[id] = props;

  return props;
};
